import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  createDashboard,
  createSlideTemplate,
  deleteDashboard,
  deleteSlideTemplate,
  getDashboards,
  getSlideTemplates,
  getDashboard,
  getDashboardUsers,
  getSlideTemplate,
  shareDashboard,
  updateDashboard,
  updateSlideTemplate
} from './dashboards.apis';
import { DashboardParams, SlideTemplateParams } from '../types/api';
import { getTemplate } from './template.apis';

export const useTemplate = (name: string) => {
  return useQuery(`tpl-${name}`, async () => {
    return getTemplate({ fileName: name });
  });
};

/**
 * Read only hooks for dashboards, if id is undefined then the react-query does not execute
 */

export const useDashboards = (params?: DashboardParams) =>
  useQuery(`dashboards-${JSON.stringify(params)}`, () => getDashboards(params));
export const useDashboardUsers = () => useQuery('dashboards-users', getDashboardUsers);

export const useDashboard = (id?: string) =>
  useQuery(`dashboard-${id}`, () => getDashboard(id || ''), { enabled: typeof id === 'string' });

export const useSlideTemplates = (params?: SlideTemplateParams) =>
  useQuery(`slidetemplates-${JSON.stringify(params)}`, () => getSlideTemplates(params));

export const useSlideTemplate = (id?: string) =>
  useQuery(`slidetemplate-${id}`, () => getSlideTemplate(id || ''), {
    enabled: typeof id === 'string'
  });
/**
 * Mutations for the dashboard items
 */
export function useDashboardMutations() {
  const queryClient = useQueryClient();
  const invalidateDashboards = React.useMemo(
    () => ({
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: query => query.queryKey.indexOf('dashboard') >= 0
        });
      }
    }),
    [queryClient]
  );

  return {
    add: useMutation(createDashboard, invalidateDashboards),
    update: useMutation(updateDashboard, invalidateDashboards),
    remove: useMutation(deleteDashboard, invalidateDashboards),
    share: useMutation(shareDashboard, invalidateDashboards)
  };
}

/**
 * Mutations for the slide templates items
 */

export function useSlideTemplatesMutations() {
  const queryClient = useQueryClient();
  const invalidateSlideTemplates = React.useMemo(
    () => ({
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: query => query.queryKey.indexOf('slidetemplate') >= 0
        });
      }
    }),
    [queryClient]
  );

  return {
    add: useMutation(createSlideTemplate, invalidateSlideTemplates),
    update: useMutation(updateSlideTemplate, invalidateSlideTemplates),
    remove: useMutation(deleteSlideTemplate, invalidateSlideTemplates)
  };
}
